import React, { memo, useMemo } from 'react';
import {
  Stack,
  TextField,
  FormHelperText,
  FormControl,
  Tooltip,
  Typography,
  Autocomplete
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { RemoveCircleOutline, AddCircleOutline } from '@mui/icons-material';

const CommonNameList = ({
  commonNames,
  removeCommonName,
  handleInputChange,
  addNewCommonName,
  mainKey,
  type,
  onValidate,
  ids = [],
  handleTrialIdChange
}: any) => {
  const isMoreThanOneCommonName = useMemo(() => commonNames.length > 1, [commonNames]);

  return (
    <Stack justifyContent='center'>
      <Stack alignItems='center' spacing={3}>
        <Stack spacing={3}>
          {commonNames?.map((commonName: any, index: number) => {
            const showHelper = commonName.isValidated && commonName.message.length > 0;

            // if already validated, disable the validate button since it is already validated and being valid
            const isAlreadyValidated = showHelper && commonName.isValid && !commonName.isLoading;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <Stack key={`${mainKey}-${index}`} direction='row' spacing={2} alignItems='center'>
                <Tooltip title={isMoreThanOneCommonName && `Remove Common Name ${index + 1}`}>
                  <RemoveCircleOutline
                    sx={{
                      cursor: isMoreThanOneCommonName ? 'pointer' : 'not-allowed',
                      color: isMoreThanOneCommonName ? 'red' : 'grey',
                      ...(showHelper && { mt: '-26px !important' })
                    }}
                    onClick={() => {
                      if (!isMoreThanOneCommonName || commonName.isLoading) {
                        return;
                      }

                      removeCommonName(index, mainKey);
                    }}
                  />
                </Tooltip>
                {type === 'Overlay' && (
                  <Autocomplete
                    disablePortal
                    options={ids}
                    size='small'
                    sx={{ width: 200 }}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={params => <TextField {...params} label='Trial Id' />}
                    onChange={(_, value) => handleTrialIdChange(value, index, mainKey)}
                    value={commonName?.trialId || ''}
                  />
                )}
                <FormControl>
                  <TextField
                    size='small'
                    label={`Common Name ${index + 1}`}
                    variant='outlined'
                    inputProps={{ maxLength: 300 }}
                    color={commonName.isValid ? 'success' : 'error'}
                    focused={showHelper}
                    error={showHelper && !commonName.isValid}
                    sx={{
                      width: 300,
                      ...(showHelper && {
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: commonName.isValid ? 'green' : '#d32f2f'
                          }
                        }
                      })
                    }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    InputProps={{
                      readOnly: commonName.isLoading
                    }}
                    value={commonName.title}
                    onChange={event => handleInputChange(event, index, mainKey)}
                  />
                  {showHelper && (
                    <FormHelperText sx={{ color: commonName.isValid ? 'green' : '#d32f2f' }}>
                      {commonName.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {type === 'Base' && (
                  <LoadingButton
                    variant='contained'
                    size='small'
                    loadingIndicator='Validating...'
                    loading={commonName.isLoading}
                    sx={{
                      ...(showHelper && { mt: '-26px !important' })
                    }}
                    disabled={isAlreadyValidated}
                    onClick={() => onValidate(index, mainKey, commonName.title, type)}>
                    <Stack>
                      <Typography>{isAlreadyValidated ? 'Validated' : 'Validate'}</Typography>
                    </Stack>
                  </LoadingButton>
                )}
              </Stack>
            );
          })}
        </Stack>
        <Stack direction='row' justifyContent='center' mt={2} mb={8}>
          <Tooltip title='Add new Common Name'>
            <AddCircleOutline
              sx={{ cursor: 'pointer', color: 'green', fontSize: 32 }}
              onClick={() => addNewCommonName(mainKey)}
            />
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default memo(CommonNameList);
